import React, { useMemo } from 'react'
import { Header } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingCardModel } from '../api/models/charging-card-model'
import { nullsFirst } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { ChargingCardsTableMenuIcon } from '../components/ChargingCardsTableMenuIcon'
import { LinkedWallboxesCell } from '../components/LinkedWallboxesCell'
import { ChargingCardStatusChip } from '../../shared/components/ChargingCardStatusChip'
import { useChargingCardStatusLabelMap } from './use-charging-card-status-label-map'
import { format } from '@e3dc-react/shell/Libs/date'

type UseChargingTableHeaderProps = {
  installationId: number
  isManagedMode: boolean
}

export const useChargingCardsTableHeader = ({ installationId, isManagedMode }: UseChargingTableHeaderProps): Header<ChargingCardModel>[] => {
  const { t } = useCustomTranslation()
  const chargingCardStatusLabelMap = useChargingCardStatusLabelMap()

  const tableHeader: Header<ChargingCardModel>[] = useMemo(() => {
    return [
      {
        value: 'tokenId',
        valueRender: (tokenId: string | undefined) => tokenId ?? '-',
        label: t('installation.rfidCardEditor.badgeId'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'cardName',
        label: t('emobility.chargingCards.cardName'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'user',
        valueRender: (user: string | undefined) => user ?? '-',
        label: t('emobility.chargingCards.edit.user'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'status',
        label: t('emobility.chargingCards.status.status'),
        width: 'auto',
        valueRender: (status: ChargingCardModel['status']) => {
          return <ChargingCardStatusChip status={status} />
        },
        sortFunction: nullsFirst((a: ChargingCardModel['status'], b: ChargingCardModel['status']) =>
          chargingCardStatusLabelMap[a].localeCompare(chargingCardStatusLabelMap[b])
        ),
      },
      {
        value: 'changedAt',
        valueRender: (changedAt: string) => format(new Date(changedAt), t('common.format.date-fns-date')),
        label: t('emobility.chargingCards.changed'),
        width: 'auto',
        sortFunction: nullsFirst((a: string | null, b: string | null) => (a && b ? a.localeCompare(b) : 0)),
      },
      {
        value: 'linkedWallboxes',
        valueRender: (_linkedWallboxes: ChargingCardModel['linkedWallboxes'], _index: number, rowItem: ChargingCardModel | undefined) => {
          if (!rowItem) {
            /**
             * Prevent TypeScript from complaining by handling the case when rowItem is undefined, although in practice
             * "rowItem" always returns a valid value since each row corresponds to one element in the original data array.
             */
            return <></>
          }

          return (
            <LinkedWallboxesCell
              linkedWallboxes={rowItem.linkedWallboxes}
              installationId={installationId}
              chargingCard={rowItem}
              isManagedMode={isManagedMode}
            />
          )
        },
        label: t('emobility.chargingCards.works-on'),
        width: 'auto',
        sortFunction: (wallboxesA: ChargingCardModel['linkedWallboxes'], wallboxesB: ChargingCardModel['linkedWallboxes']) => {
          const wallboxesALength = wallboxesA.length
          const wallboxesBLength = wallboxesB.length

          if (wallboxesALength && !wallboxesBLength) {
            return 1
          }

          if (!wallboxesALength && wallboxesBLength) {
            return -1
          }

          return wallboxesALength - wallboxesBLength
        },
      },
      {
        value: 'ocpiTokenId',
        label: '',
        width: 60,
        valueRender: (_ocpiTokenId: string, _index: number, rowItem: ChargingCardModel | undefined) => {
          if (!rowItem) {
            /**
             * Prevent TypeScript from complaining by handling the case when rowItem is undefined, although in practice
             * "rowItem" always returns a valid value since each row corresponds to one element in the original data array.
             */
            return <></>
          }

          return <ChargingCardsTableMenuIcon chargingCard={rowItem} installationId={installationId} />
        },
      },
    ]
  }, [t, chargingCardStatusLabelMap, installationId, isManagedMode])

  return tableHeader
}
